import {
  type ChallengeIdeaFiltersState,
  initialChallengeIdeaFiltersState,
} from "stores/challengeIdeaFilters";

import util from "utils/utils";

/** UI/session keys never stored in named presets (search, pagination, view, pane). */
const NON_PERSISTABLE_IDEA_FILTER_KEYS = [
  "query",
  "page",
  "limit",
  "ideaViewType",
  "filtersVisible",
] as const satisfies ReadonlyArray<keyof ChallengeIdeaFiltersState>;

type NonPersistableIdeaFilterKey =
  (typeof NON_PERSISTABLE_IDEA_FILTER_KEYS)[number];

export type PersistableIdeaFilters = Omit<
  ChallengeIdeaFiltersState,
  NonPersistableIdeaFilterKey
>;

/**
 * Keys excluded from the active-filters badge: non-persistable UI/session
 * keys, plus sort (which is saved in presets but does not count as a filter).
 */
const EXCLUDED_FROM_FILTER_COUNT = new Set<keyof ChallengeIdeaFiltersState>([
  ...NON_PERSISTABLE_IDEA_FILTER_KEYS,
  "sort",
  "sortDirection",
]);

export function isIdeaFilterDefault(
  filters: ChallengeIdeaFiltersState,
  key: keyof ChallengeIdeaFiltersState,
): boolean {
  return util.variablesAreEqual(
    filters[key],
    initialChallengeIdeaFiltersState[key],
  );
}

/** Number of idea filters that differ from their defaults (for the filters badge). */
export function countNonDefaultIdeaFilters(
  filters: ChallengeIdeaFiltersState,
): number {
  return (
    Object.keys(initialChallengeIdeaFiltersState) as Array<
      keyof ChallengeIdeaFiltersState
    >
  ).filter(
    (key) =>
      !EXCLUDED_FROM_FILTER_COUNT.has(key) &&
      !isIdeaFilterDefault(filters, key),
  ).length;
}

export function toPersistableIdeaFilters(
  filters: ChallengeIdeaFiltersState,
): PersistableIdeaFilters {
  const persistable = { ...filters };
  for (const key of NON_PERSISTABLE_IDEA_FILTER_KEYS) {
    delete persistable[key];
  }
  return persistable;
}

/** Partial update that clears persistable filters back to defaults. */
export function clearPersistableIdeaFilters(): PersistableIdeaFilters {
  return toPersistableIdeaFilters(initialChallengeIdeaFiltersState);
}

/** Partial update that applies a saved preset and resets pagination. */
export function applySavedIdeaFilters(
  filters: PersistableIdeaFilters | Partial<PersistableIdeaFilters>,
): Partial<ChallengeIdeaFiltersState> {
  return { ...clearPersistableIdeaFilters(), ...filters, page: 1 };
}
